import { Button, Chip, CircularProgress, Divider, MenuItem, Stack, Theme, Typography } from '@mui/material';
import { BackButton, Dialog, CampaignStop } from 'components';
import { ContentCopyOutlined, EditOutlined, KeyboardArrowDown, KeyboardBackspace } from '@mui/icons-material';
import { CampaignState, CampaignStatus, RouteList } from 'types';
import { StyledMenu } from 'components/PageHeader/styled';
import { generatePath, Link as RouterLink } from 'react-router-dom';
import React, { useState } from 'react';
import { useCampaign, useIsMobile, useCampaignActionsByStatus } from 'hooks';
import { ReactComponent as DownloadIcon } from 'assets/icons/download_icon.svg';
import { ReactComponent as DeleteIcon } from 'assets/icons/delete_icon.svg';
import { formatDate, getCampaignStatus, getChipColor, getDateDescription } from 'utils';
import { DateTime } from 'luxon';
import { BUTTON_HEIGHT_CONSTANT } from 'shared';
import { getLookerReportDownloadUrl } from 'utils/report';

export const OverviewHeader = () => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const [isConfirmDeleteDialogOpen, setIsConfirmDeleteDialogOpen] = useState(false);
  const { isMobile } = useIsMobile();
  const { getCampaignQuery, deleteCampaignMutation, duplicateCampaignMutation } = useCampaign();
  const campaign = getCampaignQuery.data?.campaign;
  const status = getCampaignStatus({
    state: campaign?.state || CampaignState.draft,
    startDate: campaign?.startDate || null,
    endDate: campaign?.endDate || null,
    isStopped: campaign?.isStopped,
  });
  const { isDeleteAvailable, isPdfReportAvailable, isDuplicateAvailable, isEditAvailable, isStopAvailable } =
    useCampaignActionsByStatus(status);
  const isDuplicating = duplicateCampaignMutation.isPending;

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const showDeleteCampaignDialog = () => {
    setIsConfirmDeleteDialogOpen(true);
  };

  const hideDeleteCampaignDialog = () => {
    setIsConfirmDeleteDialogOpen(false);
  };

  const onDelete = (): void => {
    showDeleteCampaignDialog();
  };

  const onDownload = (): void => {
    // eslint-disable-next-line no-console
    console.log('Function not implemented.');
  };

  const deleteCampaignHandler = async () => {
    deleteCampaignMutation.mutate();
    hideDeleteCampaignDialog();
  };

  const getStatusMessage = () => {
    // Use getDateDescription for cases that match exactly
    if (status === CampaignStatus.draft || status === CampaignStatus.over || status === CampaignStatus.stopped) {
      const endDate = status === CampaignStatus.stopped ? campaign?.stoppedAt : campaign?.endDate;

      const description = getDateDescription(
        status,
        campaign?.startDate || null,
        endDate || null,
        campaign?.updatedAt || ''
      );

      // For over status, wrap the duration part in <strong>
      if (status === CampaignStatus.over) {
        const parts = description.split(' - ');
        return (
          <>
            <strong>{parts[0]}</strong> - {parts[1]}
          </>
        );
      }

      return description;
    }

    // For active and ready, we need to add end date info that getDateDescription doesn't include
    if (status === CampaignStatus.active || status === CampaignStatus.ready) {
      const baseDescription = getDateDescription(
        status,
        campaign?.startDate || null,
        campaign?.endDate || null,
        campaign?.updatedAt || ''
      );
      const endDate = DateTime.fromISO(campaign?.endDate || '');

      return (
        <>
          <strong>{baseDescription}</strong> - Endet am {formatDate(endDate, 'dd.MM.yyyy')}
        </>
      );
    }

    return null;
  };

  const { backgroundColor, color, borderColor } = getChipColor(status);

  return (
    <>
      <Stack direction={isMobile ? 'column' : 'row'} justifyContent={'space-between'}>
        <Stack alignItems={'flex-start'} gap={1}>
          <BackButton
            variant="text"
            startIcon={<KeyboardBackspace color={'primary'} />}
            sx={(theme) => ({ textTransform: 'capitalize', maxHeight: theme.spacing(6) })}
            to={RouteList.MyCampaigns}
          >
            Zurück
          </BackButton>

          <Stack direction="row" alignItems="center">
            <Typography variant="h2" mr={4}>
              {campaign?.name}
            </Typography>
            <Chip
              label={status}
              sx={(theme) => ({
                width: 'fit-content',
                maxHeight: theme.spacing(6),
                fontWeight: 700,
                backgroundColor,
                color,
                border: `1px solid`,
                borderColor,
              })}
            />
          </Stack>
          <Typography>{getStatusMessage()}</Typography>
        </Stack>
        <Stack mt={isMobile ? 5 : 0}>
          <Button variant={'contained'} endIcon={<KeyboardArrowDown />} onClick={handleClick} disabled={isDuplicating}>
            {isDuplicating ? (
              <>
                Wird dupliziert... <Divider sx={{ mx: 1.5 }} />
                <CircularProgress sx={{ color: 'grey.700' }} size={20} />
              </>
            ) : (
              'Aktionen'
            )}
          </Button>
          <StyledMenu open={open} anchorEl={anchorEl} onClose={handleClose}>
            {isEditAvailable && (
              <MenuItem
                component={RouterLink}
                to={generatePath(RouteList.Campaign, { campaignId: String(campaign?.id) })}
              >
                <Button
                  sx={(theme) => ({ maxHeight: theme.spacing(BUTTON_HEIGHT_CONSTANT) })}
                  startIcon={<EditOutlined />}
                >
                  fertigstellen
                </Button>
              </MenuItem>
            )}
            {campaign?.id && isDuplicateAvailable && (
              <MenuItem
                onClick={() => {
                  duplicateCampaignMutation.mutate(campaign.id);
                  handleClose();
                }}
              >
                <Button
                  sx={(theme) => ({ maxHeight: theme.spacing(BUTTON_HEIGHT_CONSTANT) })}
                  startIcon={<ContentCopyOutlined />}
                  data-testid={`delete-campaign-button`}
                >
                  Duplizieren
                </Button>
              </MenuItem>
            )}
            {isDeleteAvailable && (
              <MenuItem onClick={onDelete}>
                <Button
                  sx={(theme) => ({ maxHeight: theme.spacing(BUTTON_HEIGHT_CONSTANT) })}
                  startIcon={<DeleteIcon />}
                  data-testid={`delete-campaign-button`}
                >
                  Löschen
                </Button>
              </MenuItem>
            )}
            {campaign?.reportId && isPdfReportAvailable ? (
              <MenuItem onClick={onDownload}>
                <Button
                  sx={(theme) => ({ maxHeight: theme.spacing(BUTTON_HEIGHT_CONSTANT) })}
                  startIcon={<DownloadIcon />}
                  target="_blank"
                  href={getLookerReportDownloadUrl(campaign.id)}
                >
                  reporting als PDF
                </Button>
              </MenuItem>
            ) : null}
            {isStopAvailable ? (
              <MenuItem>
                <CampaignStop
                  buttonStyles={(theme: Theme) => ({ maxHeight: theme?.spacing(BUTTON_HEIGHT_CONSTANT) })}
                  status={status}
                  invoicedAmount={campaign?.invoicedAmount as number}
                  campaignId={campaign?.id as number}
                />
              </MenuItem>
            ) : null}
          </StyledMenu>
        </Stack>
      </Stack>
      <Dialog
        open={isConfirmDeleteDialogOpen}
        title="Bist du sicher, dass du die Kampagne löschen möchtest?"
        content=""
        primaryActionTitle="Ja, löschen"
        secondaryActionTitle="Nein, abbrechen"
        onSecondaryActionClick={hideDeleteCampaignDialog}
        onPrimaryActionClick={deleteCampaignHandler}
      />
    </>
  );
};
